/**
 * Flow Optimizer — capacity-rule helpers (pure, provider-agnostic).
 *
 * `parseCapacityRules` defensively coerces persisted JSON into a clean
 * `CapacityRule[]` (drops malformed items) — the read-boundary shape guard,
 * mirroring how the config serializer sanitises exclude/formula JSON.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { CapacityRule, FlowLayer } from "./voluum-flow-optimizer.types";

const LAYERS: readonly FlowLayer[] = ["paths", "landers", "offers"];

const isLayer = (v: unknown): v is FlowLayer =>
  typeof v === "string" && LAYERS.includes(v as FlowLayer);

const isPathScope = (v: unknown): v is NonNullable<CapacityRule["pathScope"]> =>
  v === "default" || v === "rules";

/** A non-negative whole number, or null for anything else. */
function bound(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : null;
}

/** Coerce unknown persisted JSON into a valid `CapacityRule[]`. */
export function parseCapacityRules(value: unknown): CapacityRule[] {
  if (!Array.isArray(value)) return [];
  const out: CapacityRule[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object") continue;
    const r = raw as Partial<CapacityRule>;
    const layers = Array.isArray(r.layers) ? r.layers.filter(isLayer) : [];
    if (layers.length === 0) continue;
    out.push({
      id: typeof r.id === "string" && r.id ? r.id : `cap_${out.length}`,
      name: typeof r.name === "string" ? r.name : "",
      layers,
      max: bound(r.max),
      // Only carried when set, so a rule with no floor of its own is
      // distinguishable from one that floors at 0 — the engine's built-in
      // per-scope default applies to the former only.
      ...(bound(r.min) === null ? {} : { min: bound(r.min) }),
      // Fill-up is only meaningful against a numeric max — a rule whose max
      // went to Ignore sheds the flag rather than persisting a dead switch.
      ...(r.fillToMax === true && bound(r.max) !== null
        ? { fillToMax: true }
        : {}),
      // Absent/junk scope = "default" semantics downstream (legacy rules).
      ...(isPathScope(r.pathScope) ? { pathScope: r.pathScope } : {}),
    });
  }
  return out;
}

/**
 * A capacity SLOT is what a rule occupies (one cap per slot): the paths layer
 * splits into the default bucket vs the per-rule-group cap; landers/offers
 * stay whole layers.
 */
export type CapacitySlot = "paths-default" | "paths-rules" | "landers" | "offers";

export const CAPACITY_SLOTS: readonly CapacitySlot[] = [
  "paths-default",
  "paths-rules",
  "landers",
  "offers",
];

/** The slots a rule occupies (paths expands by its scope; legacy = default). */
export function slotsOfRule(rule: CapacityRule): CapacitySlot[] {
  return rule.layers.map((layer) => {
    if (layer !== "paths") return layer;
    return (rule.pathScope ?? "default") === "rules"
      ? "paths-rules"
      : "paths-default";
  });
}

/** A rule is over its cap on a layer when it has a numeric max and count exceeds it. */
export function isOverCapacity(max: number | null, activeCount: number): boolean {
  return max !== null && activeCount > max;
}

/**
 * Why one rule's bounds cannot be satisfied, or null when they can.
 *
 * A floor ABOVE the ceiling is not merely odd, it deadlocks the pool: the
 * rotation evicts down to the cap and refuses to go below the floor at the same
 * time, and the headroom a covering promotion would need (`cap - active`) is
 * zero or less exactly when the pool sits at its cap. So nothing can ever
 * satisfy it, no queue depth helps, and every run reports the same block.
 *
 * Lives in `shared` because it has to be enforced in BOTH places — the editor
 * (so you find out while typing) and the API (so it is true regardless of
 * client). Two copies of this rule would eventually disagree.
 */
export function capacityBoundsIssue(rule: CapacityRule): string | null {
  const { min, max } = rule;
  if (typeof min !== "number" || max === null || max === undefined) return null;
  if (min <= max) return null;
  return `Min ${min} is above max ${max} — the rotation could never satisfy both.`;
}

/** The first unsatisfiable rule in a set, named, or null when all are fine. */
export function capacityRulesIssue(
  rules: ReadonlyArray<CapacityRule> | undefined,
): string | null {
  for (const rule of rules ?? []) {
    const issue = capacityBoundsIssue(rule);
    if (issue) return `${rule.name?.trim() || "Capacity rule"} — ${issue}`;
  }
  return null;
}
